import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Megaphone, Users, MessageCircle, Ticket, FileText, Bot, UserCheck,
  TrendingUp, AlertTriangle, Loader2, ExternalLink, Download, Trophy,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';
import { WeeklyRankingCard } from '../components/WeeklyRankingCard';
import { InfoTip } from '../components/InfoTip';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_LABEL = {
  whatsapp: 'WhatsApp Cloud', whatsapp_web: 'WhatsApp Web', messenger: 'Messenger',
  instagram: 'Instagram', sms: 'SMS', email: 'Email', tiktok: 'TikTok', unknown: 'Otro',
};

const MetricTile = ({ icon: Icon, label, value, hint, color, onClick, testId, info }) => (
  <div className="relative">
    <button
      data-testid={testId}
      onClick={onClick}
      className="group w-full text-left p-4 rounded-2xl bg-card border border-border/60 transition-[border-color,box-shadow,transform] hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
    >
      <Icon className={cn('w-4 h-4', color)} />
      <p className="mt-2 text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground pr-5">{label}</p>
      {hint && <p className="text-[11px] text-muted-foreground/70 mt-0.5">{hint}</p>}
      <ExternalLink className="w-3 h-3 absolute bottom-3 right-3 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
    {info && <div className="absolute top-3 right-3"><InfoTip {...info} testId={`${testId}-info`} /></div>}
  </div>
);

export default function Reports() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('single');
  const [compareIds, setCompareIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/campaigns`).then((r) => {
      setCampaigns(r.data || []);
      if (r.data?.length) setCampaignId(r.data[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/campaign/${campaignId}`);
      setReport(res.data);
    } catch {
      toast.error('No se pudo cargar el reporte');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const toggleCompare = (id) => setCompareIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length >= 6 ? prev : [...prev, id]));

  const runCompare = useCallback(async (ids) => {
    if (ids.length < 2) { setComparison(null); return; }
    setComparing(true);
    try {
      const res = await axios.get(`${API}/reports/compare?campaign_ids=${ids.join(',')}`);
      setComparison(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo comparar');
      setComparison(null);
    } finally {
      setComparing(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'compare') runCompare(compareIds);
  }, [mode, compareIds, runCompare]);

  useEffect(() => {
    if (mode === 'compare' && compareIds.length === 0 && campaigns.length >= 2) {
      setCompareIds([campaigns[0].id, campaigns[1].id]);
    }
  }, [mode, campaigns, compareIds.length]);

  const fmt = (row, value) => {
    if (value === null || value === undefined) return '-';
    if (row.format === 'percent') return `${value}%`;
    if (row.format === 'money') return `$${Number(value).toLocaleString()}`;
    return Number(value).toLocaleString();
  };

  const exportJson = () => {
    const payload = mode === 'compare' ? comparison : report;
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = mode === 'compare' ? 'comparativa-campanas.json' : `reporte-${report.campaign.name || campaignId}.json`;
    a.click();
    toast.success('Reporte descargado');
  };

  const c = report?.customers;
  const pipelineRows = c ? [
    { label: 'Leads', value: c.leads, color: 'bg-brand-green' },
    { label: 'Prospectos', value: c.prospects, color: 'bg-violet-500' },
    { label: 'Calificados', value: c.qualified, color: 'bg-fuchsia-500' },
    { label: 'Negociación', value: c.negotiation, color: 'bg-amber-500' },
    { label: 'Ganados', value: c.won, color: 'bg-emerald-500' },
    { label: 'Perdidos', value: c.lost, color: 'bg-red-500' },
  ] : [];
  const maxPipeline = Math.max(1, ...pipelineRows.map((r) => r.value || 0));

  return (
    <div className="min-h-screen bg-background" data-testid="reports-page">
      <Header title="Reportes por Campaña" subtitle="Datos aislados: Noah nunca mezcla campañas" />

      <div className="p-4 sm:p-6 space-y-5">
        <WeeklyRankingCard />

        <Card className="border-border/60">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl bg-muted/40 p-1">
              <button data-testid="reports-mode-single" onClick={() => setMode('single')}
                className={cn('px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5',
                  mode === 'single' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground')}>
                <BarChart3 className="w-3.5 h-3.5" /> Una campaña
              </button>
              <button data-testid="reports-mode-compare" onClick={() => setMode('compare')}
                className={cn('px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5',
                  mode === 'compare' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground')}>
                <Trophy className="w-3.5 h-3.5" /> Comparar
              </button>
            </div>
            <Megaphone className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300" />
            {mode === 'single' ? (
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="w-[240px] h-9 text-xs" data-testid="reports-campaign-select">
                  <SelectValue placeholder="Elige una campaña" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="reports-compare-picker">
                {campaigns.map((x) => (
                  <button key={x.id} data-testid={`reports-compare-chip-${x.id}`}
                    onClick={() => toggleCompare(x.id)}
                    className={cn('px-2.5 py-1 rounded-lg text-xs border transition-colors',
                      compareIds.includes(x.id)
                        ? 'bg-violet-500/15 text-brand-deep dark:text-brand-deep dark:text-brand-green border-violet-500/40'
                        : 'bg-muted/30 text-muted-foreground border-transparent hover:bg-muted/60')}>
                    {x.name}
                  </button>
                ))}
              </div>
            )}
            {mode === 'single' && report && (
              <>
                <Badge variant="outline" className="text-[10px]">{report.campaign.focus_type}</Badge>
                <Badge variant="outline" className="text-[10px]">Noah: {report.campaign.noah_mode}</Badge>
              </>
            )}
            <Button size="sm" variant="outline" className="ml-auto gap-1" onClick={exportJson} data-testid="reports-export">
              <Download className="w-3.5 h-3.5" /> Exportar
            </Button>
          </CardContent>
        </Card>

        {mode === 'compare' && (
          comparing ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
          ) : !comparison ? (
            <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-muted-foreground">
              Elige al menos dos campañas para compararlas lado a lado.
            </CardContent></Card>
          ) : (
            <div className="space-y-4" data-testid="reports-comparison">
              {/* Scoreboard */}
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(comparison.columns.length, 3)}, minmax(0, 1fr))` }}>
                {comparison.columns.map((col) => {
                  const isChampion = comparison.champion_id === col.id;
                  return (
                    <div key={col.id} data-testid={`reports-compare-column-${col.id}`}
                      className={cn('p-4 rounded-2xl border transition-colors',
                        isChampion ? 'border-amber-500/50 bg-amber-500/5' : 'border-border/60 bg-card')}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color || '#559975' }} />
                        <p className="text-sm font-semibold truncate">{col.name}</p>
                        {isChampion && (
                          <Badge className="ml-auto text-[10px] gap-1 bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30">
                            <Trophy className="w-3 h-3" /> Ganadora
                          </Badge>
                        )}
                      </div>
                      <p className="mt-2 text-2xl font-bold tabular-nums">{comparison.scoreboard[col.id]}</p>
                      <p className="text-[11px] text-muted-foreground">métricas ganadas de {comparison.rows.length}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-[10px]">{col.focus_type}</Badge>
                        <Badge variant="outline" className="text-[10px]">Noah: {col.noah_mode}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Metric table */}
              <Card className="border-border/60">
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60">
                        <th className="text-left p-3 font-medium text-xs text-muted-foreground">Métrica</th>
                        {comparison.columns.map((col) => (
                          <th key={col.id} className="text-right p-3 font-medium text-xs">{col.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.rows.map((row) => (
                        <tr key={row.id} className="border-b border-border/30 hover:bg-muted/20 transition-colors"
                          data-testid={`reports-compare-row-${row.id}`}>
                          <td className="p-3 text-xs">
                            {row.label}
                            {row.better === 'low' && <span className="text-muted-foreground/60"> (menos es mejor)</span>}
                          </td>
                          {comparison.columns.map((col) => {
                            const isWinner = row.winner_ids.includes(col.id);
                            return (
                              <td key={col.id} className={cn('p-3 text-right tabular-nums',
                                isWinner ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : '')}>
                                {fmt(row, row.values[col.id])}
                                {isWinner && row.lead_multiplier && row.lead_multiplier > 1 && (
                                  <span className="ml-1 text-[10px] text-emerald-700 dark:text-emerald-400/80">×{row.lead_multiplier}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Objections side by side */}
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(comparison.columns.length, 3)}, minmax(0, 1fr))` }}>
                {comparison.columns.map((col) => (
                  <Card key={col.id} className="border-border/60">
                    <CardContent className="p-4">
                      <p className="text-xs font-semibold flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" /> Objeciones · {col.name}
                      </p>
                      {col.top_objections.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">Sin objeciones detectadas.</p>
                      ) : (
                        <div className="space-y-1">
                          {col.top_objections.map((o, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px]">
                              <span className="capitalize truncate mr-2">{o.text}</span>
                              <span className="tabular-nums text-muted-foreground">{o.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )
        )}

        {mode === 'single' && (loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        ) : !report ? (
          <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-muted-foreground">
            Crea una campaña para ver reportes.
          </CardContent></Card>
        ) : (
          <>
            {/* Headline metrics — cada tarjeta enlaza a la pantalla de origen */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reports-metrics">
              <MetricTile testId="reports-metric-conversion" icon={TrendingUp} color="text-emerald-700 dark:text-emerald-400"
                label="Tasa de cierre" value={`${report.conversion_rate}%`}
                hint={`${report.customers.won} de ${report.customers.total} clientes`}
                info={{
                  title: 'Tasa de cierre',
                  text: 'Porcentaje de clientes de esta campaña que llegaron a "Ganado". Se recalcula cada vez que cambias la etapa de un cliente.',
                  source: 'Clientes de la campaña con estado closed_won / total de clientes',
                  to: '/deals', linkLabel: 'Ver pipeline de ventas',
                }}
                onClick={() => navigate('/deals')} />
              <MetricTile testId="reports-metric-customers" icon={Users} color="text-brand-deep dark:text-brand-green"
                label="Clientes en la campaña" value={report.customers.total}
                hint="Ver en Clientes"
                info={{
                  title: 'Clientes en la campaña',
                  text: 'Total de contactos asignados a esta campaña. Noah nunca mezcla clientes entre campañas.',
                  source: 'Colección de clientes filtrada por campaign_id',
                  to: '/customers', linkLabel: 'Abrir Clientes',
                }}
                onClick={() => navigate('/customers')} />
              <MetricTile testId="reports-metric-chats" icon={MessageCircle} color="text-brand-deep dark:text-brand-deep dark:text-brand-green"
                label="Conversaciones" value={report.chats.total}
                hint={`Noah ${report.chats.handled_by_noah} · Humano ${report.chats.handled_by_human}`}
                info={{
                  title: 'Conversaciones',
                  text: 'Chats abiertos en cualquier canal (WhatsApp, Messenger, Instagram, SMS, email) para esta campaña, separados entre los que atiende Noah y los que tomó un humano.',
                  source: 'Inbox unificado · campo handled_by de cada chat',
                  to: '/inbox', linkLabel: 'Abrir Inbox',
                }}
                onClick={() => navigate('/inbox')} />
              <MetricTile testId="reports-metric-tickets" icon={Ticket} color="text-amber-700 dark:text-amber-400"
                label="Tickets" value={report.tickets.total}
                hint={`${report.tickets.open} abiertos · ${report.tickets.high_priority} urgentes`}
                info={{
                  title: 'Tickets',
                  text: 'Casos escalados a un humano, creados por Noah cuando detecta urgencia o por tus reglas de automatización.',
                  source: 'Tablero de Tickets · prioridad y estado de cada caso',
                  to: '/tickets', linkLabel: 'Abrir Tickets',
                }}
                onClick={() => navigate('/tickets')} />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {/* Pipeline */}
              <Card className="border-border/60">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" /> Embudo de la campaña
                    <InfoTip title="Embudo de la campaña" text="Cuántos clientes hay en cada etapa del pipeline en este momento. La barra es relativa a la etapa más poblada." source="Etapa (status) de cada cliente de la campaña" to="/bubbles" linkLabel="Abrir pipeline visual" />
                    <button onClick={() => navigate('/bubbles')} className="ml-auto text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1" data-testid="reports-pipeline-link">
                      Abrir pipeline <ExternalLink className="w-3 h-3" />
                    </button>
                  </p>
                  <div className="space-y-2.5">
                    {pipelineRows.map((r) => (
                      <div key={r.label} data-testid={`reports-funnel-${r.label}`}>
                        <div className="flex justify-between text-xs mb-1">
                          <span>{r.label}</span><span className="tabular-nums font-medium">{r.value || 0}</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                          <div className={cn('h-full rounded-full transition-[width] duration-700', r.color)}
                            style={{ width: `${((r.value || 0) / maxPipeline) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Objections */}
              <Card className="border-border/60">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400" /> Principales objeciones detectadas por Noah
                    <InfoTip title="Objeciones detectadas" text="Noah lee cada conversación y extrae los motivos por los que el cliente duda (precio, tiempo, confianza...). Aquí se ordenan por frecuencia." source="Inteligencia de Noah · campo objections de cada cliente" to="/deals" linkLabel="Ver inteligencia de Noah" />
                    <button onClick={() => navigate('/deals')} className="ml-auto text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1" data-testid="reports-objections-link">
                      Ver inteligencia <ExternalLink className="w-3 h-3" />
                    </button>
                  </p>
                  {report.top_objections.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Noah aún no ha detectado objeciones recurrentes en esta campaña.</p>
                  ) : (
                    <div className="space-y-2" data-testid="reports-objections">
                      {report.top_objections.map((o, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <Badge variant="secondary" className="text-[10px] shrink-0">#{i + 1}</Badge>
                          <span className="text-xs flex-1 capitalize">{o.text}</span>
                          <Progress value={(o.count / report.top_objections[0].count) * 100} className="w-20 h-1.5" />
                          <span className="text-xs tabular-nums text-muted-foreground w-6 text-right">{o.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Channels */}
              <Card className="border-border/60">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold flex items-center gap-2 mb-3">
                    <MessageCircle className="w-4 h-4 text-emerald-700 dark:text-emerald-400" /> Conversaciones por canal
                    <InfoTip title="Conversaciones por canal" text="Reparto de chats según el canal por el que entró el cliente. Útil para saber dónde invertir." source="Campo platform de cada chat" to="/connections" linkLabel="Estado de canales" />
                    <button onClick={() => navigate('/connections')} className="ml-auto text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1" data-testid="reports-channels-link">
                      Estado de canales <ExternalLink className="w-3 h-3" />
                    </button>
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="reports-channels">
                    {Object.entries(report.chats.by_channel || {}).map(([k, v]) => (
                      <button key={k} onClick={() => navigate('/inbox')}
                        className="px-3 py-2 rounded-xl bg-muted/25 hover:bg-muted/50 transition-colors text-left">
                        <p className="text-sm font-bold tabular-nums">{v}</p>
                        <p className="text-[11px] text-muted-foreground">{CHANNEL_LABEL[k] || k}</p>
                      </button>
                    ))}
                    {Object.keys(report.chats.by_channel || {}).length === 0 && (
                      <p className="text-xs text-muted-foreground">Sin conversaciones todavía.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Noah performance */}
              <Card className="border-border/60">
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Bot className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" /> Desempeño de Noah
                    <InfoTip title="Desempeño de Noah" text="Mensajes que escribió Noah frente a los del cliente, chats que tuvo que tomar un humano y cotizaciones generadas." source="Mensajes y cotizaciones de la campaña" to="/analytics" linkLabel="Ver Analytics" />
                    <button onClick={() => navigate('/analytics')} className="ml-auto text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1" data-testid="reports-analytics-link">
                      Ver Analytics <ExternalLink className="w-3 h-3" />
                    </button>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-muted/20">
                      <p className="text-xl font-bold tabular-nums">{report.messages.from_noah}</p>
                      <p className="text-[11px] text-muted-foreground">Mensajes de Noah</p>
                    </div>
                    <div className="p-3 rounded-xl bg-muted/20">
                      <p className="text-xl font-bold tabular-nums">{report.messages.from_customers}</p>
                      <p className="text-[11px] text-muted-foreground">Mensajes de clientes</p>
                    </div>
                    <button onClick={() => navigate('/inbox')} className="p-3 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors text-left">
                      <p className="text-xl font-bold tabular-nums flex items-center gap-1"><UserCheck className="w-4 h-4 text-amber-700 dark:text-amber-400" />{report.chats.handled_by_human}</p>
                      <p className="text-[11px] text-muted-foreground">Chats con humano</p>
                    </button>
                    <button onClick={() => navigate('/invoices')} className="p-3 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors text-left">
                      <p className="text-xl font-bold tabular-nums flex items-center gap-1"><FileText className="w-4 h-4 text-brand-deep dark:text-brand-green" />{report.quotes.total}</p>
                      <p className="text-[11px] text-muted-foreground">Cotizaciones ({report.quotes.accepted} aceptadas)</p>
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Trophy className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />
                    Generado {new Date(report.generated_at).toLocaleString('es')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </>
        ))}
      </div>
    </div>
  );
}
